<template>
    <div class="edit-field" :class="{wide:wide || type=='textarea', failed:status=='error'}">

        <div class="field-top">
            <span v-if="readonly" class="field-label">{{ label }}</span>
            <label v-else class="field-label" :for="uid">{{ label }}</label>
            <span v-if="hint" class="field-info material-symbols-outlined" :title="hint" aria-hidden="true">info</span>
            <span class="field-status" :class="status">
                <template v-if="status=='saving'">Saving…</template>
                <template v-else-if="status=='saved'"><span class="material-symbols-outlined">check</span>Saved</template>
            </span>
            <a v-if="link" class="field-open" :href="link" target="_blank" rel="noopener" title="Open">
                <span class="material-symbols-outlined">open_in_new</span>
            </a>
        </div>

        <div v-if="readonly" class="field-value">{{ shown }}</div>

        <label v-else-if="type=='checkbox'" class="field-switch">
            <input :id="uid" type="checkbox" role="switch" v-model="draft" :disabled="status=='saving'"
                   :aria-describedby="hint ? hint_id : null" @change="commit()">
            <span class="switch-track" aria-hidden="true"></span>
        </label>

        <select v-else-if="type=='select'" :id="uid" v-model="draft" :aria-describedby="hint ? hint_id : null" @change="commit()">
            <option v-if="!options.includes(draft)" :value="draft">{{ draft==='' ? '(none)' : draft }}</option>
            <option v-for="option in options" :key="option" :value="option">{{ option }}</option>
        </select>

        <!-- A dropdown that also takes free text. Unlike a datalist, it lists every option
             whatever is already typed, until you start typing. -->
        <div v-else-if="type=='combobox'" class="field-combobox" :class="{open:list_shown}">
            <input :id="uid" ref="combobox" type="text" v-model="draft" role="combobox" autocomplete="off"
                   aria-autocomplete="list" :aria-expanded="list_shown ? 'true' : 'false'" :aria-controls="list_id"
                   :aria-activedescendant="list_shown && active>=0 ? `${list_id}-${active}` : null"
                   :aria-describedby="hint ? hint_id : null"
                   @input="typing(); filter_list()" @change="commit()" @click="open_list()"
                   @keydown="list_keys($event)" @blur="close_list()">
            <button type="button" class="combobox-toggle" tabindex="-1" aria-label="Show the options"
                    @mousedown.prevent @click="toggle_list()">
                <span class="material-symbols-outlined">expand_more</span>
            </button>
            <ul v-show="list_shown" :id="list_id" class="combobox-list" role="listbox" :aria-label="label">
                <li v-for="(option,index) in listed" :key="option" :id="`${list_id}-${index}`" role="option"
                    :aria-selected="option==draft ? 'true' : 'false'" :class="{active:index==active}"
                    @mousedown.prevent="choose(option)">{{ option }}</li>
            </ul>
        </div>

        <textarea v-else-if="type=='textarea'" :id="uid" v-model="draft" rows="3"
                  :aria-describedby="hint ? hint_id : null" @input="typing()" @change="commit()"></textarea>

        <input v-else :id="uid" :type="type" v-model="draft"
               :step="type=='number' ? step : null"
               :list="suggestions.length ? list_id : null"
               :placeholder="placeholder || null"
               :aria-describedby="hint ? hint_id : null"
               @input="typing()" @change="commit()">

        <datalist v-if="suggestions.length" :id="list_id">
            <option v-for="suggestion in suggestions" :key="suggestion" :value="suggestion"></option>
        </datalist>

        <p v-if="error" class="field-error" role="alert">
            <span>{{ error }}</span>
            <button type="button" @click.stop="revert()">Undo</button>
        </p>
        <p v-else-if="warning" class="field-warning">
            <span class="material-symbols-outlined">warning</span>
            <span>{{ warning }}</span>
        </p>
        <!-- Shown only while the field is focused, so the window stays quiet; the ⓘ carries it on hover. -->
        <p v-if="hint" :id="hint_id" class="field-hint">{{ hint }}</p>

    </div>
</template>


<script>
    let fields=0    // numbers each field, for its label `for` and its option list / hint ids

    // Saves queue per row and field, not per input: when several inputs each edit a part of
    // one field (`read`/`write`), a save waits for the one before it and builds on what it stored.
    const queues=new WeakMap()
</script>


<script setup>

    import {ref,computed,watch,inject,nextTick,onBeforeUnmount} from 'vue'
    import axios from 'axios'

    const props=defineProps({
        object:{type:Object,required:true},         // the row being edited - needs `endpoint` and `id`
        field:{type:String,required:true},
        label:{type:String,required:true},
        type:{type:String,default:'text'},          // text | textarea | number | email | tel | checkbox | select | combobox
        readonly:{type:Boolean,default:false},
        options:{type:Array,default:()=>[]},        // the choices of a select, or the list a combobox offers
        suggestions:{type:Array,default:()=>[]},    // offered while typing free text, never enforced
        check:{type:Function,default:null},         // value -> warning to show under the field, or ''
        display:{type:Function,default:null},       // value -> text, for read-only fields
        href:{type:Function,default:null},          // value -> address for an "Open" link, or null
        read:{type:Function,default:null},          // stored value -> the part of it this input edits
        write:{type:Function,default:null},         // (input, stored value) -> value to save, rest kept
        hint:{type:String,default:''},
        step:{type:String,default:'any'},
        placeholder:{type:String,default:''},
        wide:{type:Boolean,default:false},
    })

    let edit_window=inject('edit_window',null)
    let uid=`edit-field-${++fields}`
    let list_id=`${uid}-options`
    let hint_id=`${uid}-hint`

    let draft=ref(to_input(props.object[props.field]))
    let status=ref('idle')          // idle | saving | saved | error
    let error=ref('')
    let editing=false               // from the first keystroke until the value is saved or undone
    let in_flight=0
    let saved_timer=null

    let combobox=ref(null)
    let listing=ref(false)          // the combobox's list is open
    let filtering=ref(false)        // typed since it opened: list only the options that match
    let active=ref(-1)              // the option the arrow keys are on

    // The server rewrites values on save (cellphone is stripped, payment_method gains the
    // payer, hourly_rate_usd follows hourly_rate): follow it unless this field is mid-edit.
    watch(()=>props.object[props.field],value=>{
        if(!editing){
            draft.value=to_input(value)
        }
    })

    // Select options can arrive after the window opens - re-pick the stored value then.
    watch(()=>props.options,()=>{
        if(!editing){
            draft.value=to_input(props.object[props.field])
        }
    })

    let warning=computed(()=>props.check ? props.check(draft.value) : '')

    let shown=computed(()=>{
        let value=props.object[props.field]
        if(props.display){
            return props.display(value)
        }
        return value===null || value===undefined || value==='' ? '—' : value
    })

    let link=computed(()=>props.href ? props.href(props.object[props.field]) : null)

    let listed=computed(()=>{
        if(!filtering.value){
            return props.options
        }
        let text=String(draft.value).trim().toLowerCase()
        return props.options.filter(option=>option.toLowerCase().includes(text))
    })

    let list_shown=computed(()=>listing.value && listed.value.length>0)

    watch(active,index=>{
        nextTick(()=>{
            let option=document.getElementById(`${list_id}-${index}`)
            if(option && option.scrollIntoView){
                option.scrollIntoView({block:'nearest'})
            }
        })
    })


    function to_input(value){
        if(props.read){
            value=props.read(value)
        }
        if(props.type=='checkbox'){
            return Boolean(value)
        }
        if(value===null || value===undefined){
            return ''
        }
        let text=String(value)
        if(props.type=='select'){
            // Stored values often carry stray spaces (' Fixed Monthly '). Every reader trims or
            // substring-matches them, so show the option they mean, not an unknown value.
            let match=props.options.find(option=>option.trim()==text.trim())
            if(match!==undefined){
                return match
            }
        }
        return text
    }


    function same_as_saved(){
        let saved=to_input(props.object[props.field])
        if(props.type=='number' && draft.value!=='' && saved!==''){
            return Number(draft.value)==Number(saved)
        }
        return draft.value===saved
    }


    function typing(){
        editing=true
    }


    function open_list(){
        if(!listing.value){
            filtering.value=false
            active.value=-1
            listing.value=true
        }
    }


    function filter_list(){
        filtering.value=true
        active.value=-1
        listing.value=true
    }


    function close_list(){
        listing.value=false
        active.value=-1
    }


    function toggle_list(){
        combobox.value.focus()
        if(listing.value){
            close_list()
        }else{
            open_list()
        }
    }


    function choose(option){
        draft.value=option
        close_list()
        commit()
    }


    function list_keys(event){
        let count=listed.value.length
        if(event.key=='ArrowDown' || event.key=='ArrowUp'){
            event.preventDefault()
            if(!list_shown.value){
                open_list()
                return
            }
            let step=event.key=='ArrowDown' ? 1 : -1
            active.value=active.value<0 ? (step>0 ? 0 : count-1) : (active.value+step+count)%count
        }else if(event.key=='Enter' && list_shown.value && active.value>=0){
            event.preventDefault()
            choose(listed.value[active.value])
        }else if(event.key=='Escape' && list_shown.value){
            event.stopPropagation()     // closes the list, not the window
            close_list()
        }
    }


    function commit(){
        // Back to what is stored: nothing to send. (While a save is still out, the stored
        // value is about to change, so the comparison would be against a stale value.)
        if(!in_flight && same_as_saved()){
            if(status.value=='error'){
                revert()
            }
            editing=false
            return
        }
        editing=true
        in_flight++
        let value=draft.value
        let save=enqueue(()=>send(value))
        if(edit_window){
            edit_window.track(save)
        }
    }


    // One field's saves go out one at a time, in order.
    function enqueue(job){
        if(!queues.has(props.object)){
            queues.set(props.object,{})
        }
        let field_queues=queues.get(props.object)
        let save=(field_queues[props.field] || Promise.resolve()).then(job)
        field_queues[props.field]=save
        return save
    }


    async function send(value){
        status.value='saving'
        error.value=''
        try{
            let response=await axios.patch('/admin/patch/',{
                object:{endpoint:props.object.endpoint,id:props.object.id},
                key:props.field,
                // Built as it goes out, so a part another input just saved is kept.
                value:props.write ? props.write(value,props.object[props.field]) : value,
            })
            if(draft.value===value){
                editing=false
            }
            Object.assign(props.object,response.data)
            if(!editing){
                draft.value=to_input(props.object[props.field])    // the stored form: '150' comes back '150.00'
            }
            status.value='saved'
            clearTimeout(saved_timer)
            saved_timer=setTimeout(()=>{
                if(status.value=='saved'){
                    status.value='idle'
                }
            },1500)
            if(edit_window){
                edit_window.saved(uid)
            }
        }catch(e){
            status.value='error'
            error.value=error_message(e)
            if(edit_window){
                edit_window.failed(uid,props.label)
            }
        }finally{
            in_flight--
        }
    }


    // Admin.patch answers a bad value with 400 {errors:{<field>:[...]}}; permission problems
    // come back as DRF's {detail}.
    function error_message(e){
        let body=e.response && e.response.data
        if(body && body.errors && body.errors[props.field]){
            return [].concat(body.errors[props.field]).join(' ')
        }
        if(body && (body.error || body.detail)){
            return body.error || body.detail
        }
        if(e.response){
            return `Not saved - the server answered ${e.response.status}.`
        }
        return 'Not saved - the server could not be reached.'
    }


    function revert(){
        editing=false
        error.value=''
        if(status.value=='error'){
            status.value='idle'
        }
        draft.value=to_input(props.object[props.field])
        if(edit_window){
            edit_window.reverted(uid)
        }
    }


    onBeforeUnmount(()=>{
        clearTimeout(saved_timer)
    })

</script>


<style scoped>

    .edit-field{
        display:flex;
        flex-direction:column;
        min-width:0;
    }

    .wide{
        grid-column:1 / -1;
    }

    .field-top{
        display:flex;
        align-items:center;
        gap:6px;
        min-height:18px;
        margin-bottom:6px;
    }

    .field-label{
        font-size:12.5px;
        font-weight:500;
        color:var(--ew-muted);
    }

    .field-info{
        font-size:15px;
        color:#a9a59d;
        cursor:help;
    }

    .field-status{
        display:inline-flex;
        align-items:center;
        gap:2px;
        margin-left:auto;
        font-size:12px;
        color:var(--ew-muted);
    }

    .field-status.saved{
        color:var(--ew-ok);
    }

    .field-status .material-symbols-outlined{
        font-size:15px;
    }

    .field-open{
        display:inline-flex;
        color:var(--ew-muted);
        text-decoration:none;
    }

    .field-open:hover{
        color:var(--ew-focus);
    }

    .field-open .material-symbols-outlined{
        font-size:16px;
    }

    input:not([type=checkbox]),
    select,
    textarea{
        width:100%;
        min-height:36px;
        padding:7px 10px;
        font-size:14px;
        color:var(--ew-text);
        background:#ffffff;
        border:1px solid var(--ew-field-line);
        border-radius:6px;
        transition:border-color 0.15s ease-in-out,box-shadow 0.15s ease-in-out;
    }

    input:not([type=checkbox]):hover,
    select:hover,
    textarea:hover{
        border-color:#bdb6ab;
    }

    input:not([type=checkbox]):focus,
    select:focus,
    textarea:focus{
        outline:none;
        border-color:var(--ew-focus);
        box-shadow:0 0 0 3px rgba(6,115,158,0.15);
    }

    .failed input:not([type=checkbox]),
    .failed select,
    .failed textarea{
        border-color:var(--ew-error);
        box-shadow:0 0 0 3px rgba(179,38,30,0.1);
    }

    textarea{
        min-height:76px;
        line-height:1.4;
        resize:vertical;
    }

    .field-combobox{
        position:relative;
    }

    .field-combobox input{
        padding-right:34px;
    }

    .combobox-toggle{
        position:absolute;
        top:1px;
        right:1px;
        bottom:1px;
        display:flex;
        align-items:center;
        justify-content:center;
        width:32px;
        padding:0;
        word-break:normal;
        color:var(--ew-muted);
        background:none;
        border:none;
        border-radius:0 6px 6px 0;
        box-shadow:none;
        cursor:pointer;
    }

    .combobox-toggle:hover{
        color:var(--ew-text);
        background:none;
        box-shadow:none;
    }

    .combobox-toggle .material-symbols-outlined{
        font-size:20px;
        transition:transform 0.15s ease-in-out;
    }

    .open .combobox-toggle .material-symbols-outlined{
        transform:rotate(180deg);
    }

    /* Above the fields below it, under the window's sticky header. */
    .combobox-list{
        position:absolute;
        top:calc(100% + 4px);
        right:0;
        left:0;
        z-index:1;
        max-height:248px;
        margin:0;
        padding:4px 0;
        overflow-y:auto;
        list-style:none;
        background:#ffffff;
        border:1px solid var(--ew-field-line);
        border-radius:6px;
        box-shadow:0 8px 24px rgba(0,0,0,0.12);
    }

    .combobox-list li{
        padding:7px 10px;
        font-size:14px;
        cursor:pointer;
    }

    .combobox-list li:hover,
    .combobox-list li.active{
        background:var(--ew-bg);
    }

    .combobox-list li[aria-selected=true]{
        font-weight:600;
    }

    /* A visually hidden checkbox driving a drawn track, so it works the same in every browser. */
    .field-switch{
        position:relative;
        display:inline-flex;
        align-items:center;
        align-self:flex-start;
        height:36px;
        cursor:pointer;
    }

    .field-switch input{
        position:absolute;
        width:36px;
        height:20px;
        margin:0;
        opacity:0;
        cursor:pointer;
    }

    .switch-track{
        position:relative;
        width:36px;
        height:20px;
        background:#cbc5bb;
        border-radius:999px;
        transition:background 0.15s ease-in-out;
    }

    .switch-track::after{
        content:'';
        position:absolute;
        top:2px;
        left:2px;
        width:16px;
        height:16px;
        background:#ffffff;
        border-radius:50%;
        box-shadow:0 1px 2px rgba(0,0,0,0.3);
        transition:transform 0.15s ease-in-out;
    }

    .field-switch input:checked + .switch-track{
        background:var(--ew-focus);
    }

    .field-switch input:checked + .switch-track::after{
        transform:translateX(16px);
    }

    .field-switch input:focus-visible + .switch-track{
        outline:2px solid var(--ew-focus);
        outline-offset:2px;
    }

    .field-switch input:disabled + .switch-track{
        opacity:0.6;
    }

    .field-value{
        min-height:36px;
        padding:8px 0;
        overflow-wrap:anywhere;
    }

    .field-error,
    .field-warning,
    .field-hint{
        margin-top:6px;
        font-size:12.5px;
        line-height:1.4;
    }

    .field-error{
        display:flex;
        flex-wrap:wrap;
        align-items:baseline;
        gap:4px 10px;
        color:var(--ew-error);
    }

    .field-error button{
        padding:0;
        font-size:12.5px;
        word-break:normal;
        color:var(--ew-focus);
        background:none;
        border:none;
        box-shadow:none;
        text-decoration:underline;
    }

    .field-error button:hover{
        color:var(--ew-text);
        background:none;
        box-shadow:none;
    }

    .field-warning{
        display:flex;
        align-items:flex-start;
        gap:6px;
        color:var(--ew-warn);
    }

    .field-warning .material-symbols-outlined{
        font-size:16px;
    }

    .field-hint{
        display:none;
        color:var(--ew-muted);
    }

    .edit-field:focus-within .field-hint{
        display:block;
    }

</style>
